import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, defer, filter, finalize, map, share, timer } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../../../environments/environment';
import {
  VideoPlaybackState,
  isTypedSocketMessage,
  isVideoPlaybackStateMessage,
} from '../../../models/common/video-playback.model';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
  private socket$!: WebSocketSubject<any>;
  private readonly messagesSubject = new Subject<any>();
  /** Live-score updates (untyped messages). */
  public messages$: Observable<any> = this.messagesSubject.pipe(
    filter((message) => !isTypedSocketMessage(message))
  );
  /**
   * A streamer paused or resumed a video. While anything listens (an open watch screen), the socket skips the idle
   * close and reconnects quickly, so a pause/resume isn't lost in a reconnect gap.
   */
  public readonly playbackState$: Observable<VideoPlaybackState> = defer(() => {
    this.setKeepOpen(true);
    return this.messagesSubject.pipe(
      filter(isVideoPlaybackStateMessage),
      map((message) => message.data)
    );
  }).pipe(
    finalize(() => this.setKeepOpen(false)),
    share()
  );

  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  /**
   * Whether live messages can arrive: true once open; false after an error or an unexpected close.
   * The idle close (no message for a minute) keeps it true: nothing is happening, and it reconnects by itself.
   */
  public connected$: Observable<boolean> = this.connectedSubject.asObservable();
  /** Set while the idle timer closes the socket, so that close is not reported as a disconnect. */
  private idleClosing = false;

  private readonly WS_URL = environment.socket_url;

  private readonly idleTimeout = 60000; // 1 minute idle
  private readonly reconnectDelay = 15000;
  private readonly keepOpenReconnectDelay = 2000;
  private idleTimerSub?: Subscription;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private isOpen = false;
  private keepOpen = false;

  private manualClose = false; // flag to indicate manual/browser close

  constructor() {
    this.connect();

    // Close socket if user closes tab/browser
    globalThis.window.addEventListener('beforeunload', () => {
      this.manualClose = true;
      this.closeConnection();
    });
  }

  private connect(): void {
    this.socket$ = webSocket({
      url: this.WS_URL,
      openObserver: {
        next: () => {
          this.isOpen = true;
          this.idleClosing = false;
          this.connectedSubject.next(true);
          this.startIdleTimer();
        },
      },
      closeObserver: {
        next: (event) => {
          console.warn('[WebSocket] Connection closed', event);
          this.isOpen = false;
          this.clearIdleTimer();
          if (!this.idleClosing) {
            this.connectedSubject.next(false);
          }
          this.idleClosing = false;

          // Only auto-reconnect if it wasn't a manual/browser close
          if (!this.manualClose) {
            this.scheduleReconnect(this.keepOpen ? this.keepOpenReconnectDelay : this.reconnectDelay);
          }

          // Reset manualClose flag for future reconnects
          this.manualClose = false;
        }
      }
    });

    this.socket$.subscribe({
      next: (msg) => {
        
        this.messagesSubject.next(msg);
        this.resetIdleTimer();
      },
      error: (err) => {
        console.error('[WebSocket] Error:', err);
        this.connectedSubject.next(false); // a failed socket is down, so listeners can fall back to polling
        this.clearIdleTimer();
        this.socket$.complete();
      }
    });
  }

  public sendMessage(msg: any): void {
    if (this.socket$) {
      this.socket$.next(msg);
      this.resetIdleTimer();
    }
  }

  public closeConnection(): void {
    
    this.clearIdleTimer();
    if (this.socket$) {
      this.socket$.complete(); // closes the socket
    }
  }

  private scheduleReconnect(delay: number): void {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  /** Hold the socket open for playbackState$ listeners; reconnect right away if it is closed. */
  private setKeepOpen(keepOpen: boolean): void {
    this.keepOpen = keepOpen;
    if (!keepOpen) {
      if (this.isOpen) {
        this.startIdleTimer();
      }
      return;
    }
    this.clearIdleTimer();
    if (!this.isOpen && this.reconnectTimer) {
      this.scheduleReconnect(0);
    }
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();
    if (this.keepOpen) {
      return;
    }
    this.idleTimerSub = timer(this.idleTimeout).subscribe(() => {
      console.warn(`[WebSocket] Idle for ${this.idleTimeout / 1000}s — closing connection`);
      this.manualClose = false; // idle close is not manual, allow reconnect
      this.idleClosing = true;
      this.closeConnection();
    });
  }

  private resetIdleTimer(): void {
    this.startIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimerSub) {
      this.idleTimerSub.unsubscribe();
      this.idleTimerSub = undefined;
    }
  }

  ngOnDestroy(): void {
    this.manualClose = true;
    clearTimeout(this.reconnectTimer);
    this.closeConnection();
    globalThis.window.removeEventListener('beforeunload', () => this.closeConnection());
  }
}
